// React
import { useEffect } from "react"

// Components
import { PrintTransaction } from "components"

// Form
import { useFormContext, useWatch } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"

// Types
import type { GroupName } from "../../types"

// Utils
import { convertNumber, useAccounts, useHandleAuth, useLocation } from "utils"
import { getAcc } from "pages/Finance/Transaction/SpendMoney/utils"
import { useTrader } from "../../components/widgets/FormSection/components/Trader/utils"
import { type FormType, getAmount, getAmountDiff } from "../../utils"

export function Print(): JSX.Element {
  // Form
  const { control } = useFormContext<FormType>()
  const [amount, card_id, coa_id, datajournal, group_card, location_id, memo, reference_no, tax_inclusive, transaction_date, trx_code] = useWatch({
    control,
    name: ["amount", "card_id", "coa_id", "datajournal", "group_card", "location_id", "memo", "reference_no", "tax_inclusive", "transaction_date", "trx_code"]
  })

  // Hooks
  const account = useAccounts()
  const location = useLocation()
  const trader = useTrader()
  const { userTokenData } = useHandleAuth()
  
  // Vars
  const isLoading: boolean = account.isLoading || location.isLoading || trader.isLoading
  const selectedAccount = account.data.find(item => item.value === coa_id)
  const selectedLocation = location.data.find(item => item.value === location_id)
  const selectedTrader = trader.data.find(item => item.value === card_id)
  const { sub_total, tax } = getAcc(datajournal, tax_inclusive)
  const amount_diff = getAmountDiff(amount, sub_total, tax)
  const diff = amount_diff - (!tax_inclusive ? tax : 0)

  useEffect(() => {
    if (Boolean(group_card)) {
      trader.refetch(group_card as GroupName)
    }

    // eslint-disable-next-line
  }, [group_card])

  return (
    <PrintTransaction loading={isLoading}>
      <section className="flex flex-col gap-5">
        <section>
          <div className="w-fit bg-[#797979] text-white px-10 py-3">RECEIVE MONEY | PAY CHEQUE</div>
          <div className="border-y py-5 font-bold text-center">{userTokenData()?.company_name}</div>
        </section>

        <section className="px-3 grid grid-cols-2 gap-3">
          <InfoSection
            list={[
              {
                label: "Beneficiary Bank",
                value: selectedAccount?.label ?? "-"
              },
              {
                label: "Payment From",
                value: selectedTrader?.label ?? "-"
              },
              {
                label: "Location",
                value: selectedLocation?.label ?? "-"
              }
            ]}
          />

          <InfoSection
            list={[
              {
                label: "ID Number",
                value: trx_code
              },
              {
                label: "Transaction Date",
                value: moment(transaction_date).format("DD/MM/YYYY")
              },
              {
                label: "Reference No",
                value: reference_no
              },
            ]}
          />
        </section>

        <table className="table header-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Account Register</th>
              <th>Memo</th>
              <th>Amount</th>
              <th>Tax</th>
            </tr>
          </thead>

          <tbody>
            {datajournal.map((item, key) => {
              // Vars
              const amount = getAmount(item.amount, true, item.rate, tax_inclusive)

              return (
                <tr key={key}>
                  <td className="text-center">{key + 1}</td>
                  <td>{item.coa_name}</td>
                  <td>{item.memo}</td>
                  <td className="text-right">{convertNumber(amount).intoCurrency}</td>
                  <td>{item.tax_name}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <section className="px-3 grid grid-cols-2 gap-3">
          <InfoSection
            list={[
              {
                label: "Memo",
                value: memo
              }
            ]}
          />

          <InfoSection
            class={{ value: "text-right" }}
            list={[
              {
                label: "Total Allocated",
                value: convertNumber(sub_total).intoCurrency
              },
              {
                label: "Tax Amount",
                value: convertNumber(tax).intoCurrency
              },
              {
                label: "Total Paid",
                value: convertNumber(amount + (!tax_inclusive ? tax : 0)).intoCurrency
              },
              {
                label: "Out of Balance",
                value: convertNumber(diff).intoCurrency
              }
            ]}
          />
        </section>
      </section>
    </PrintTransaction>
  )
}

function InfoSection(params: {
  class?: { value?: string }
  list: {
    label: string
    value: string
  }[]
}): JSX.Element {
  return (
    <table className="h-fit table border-b">
      <tbody>
        {params.list.map((item, key) => (
          <tr key={key}>
            <td className="font-bold">{item.label}</td>
            <td className={params?.class?.value}>{item.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}